import {
  MAX_AGENT_ATTACHMENT_BYTES,
  MAX_AGENT_ATTACHMENTS_TOTAL_BYTES,
  agentImageDimensionError,
  inspectAgentAttachmentImage,
  type AgentMessageAttachmentKind,
} from 'shared';

export type AgentAttachmentKind = AgentMessageAttachmentKind;

const imageTypes = ['image/png', 'image/jpeg', 'image/webp'];
const extensions: Record<Exclude<AgentAttachmentKind, 'image'>, string[]> = {
  document: ['pdf', 'docx', 'xlsx', 'csv', 'pptx', 'txt', 'md'],
  audio: ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a', 'aiff'],
  video: ['mp4', 'mpeg', 'mpg', 'mov', 'webm'],
};

export const ALL_ATTACHMENT_KINDS: AgentAttachmentKind[] = ['image', 'document', 'audio', 'video'];
const validatedImages = new WeakSet<File>();

export function agentAttachmentKind(file: {
  name: string;
  type: string;
}): AgentAttachmentKind | null {
  if (imageTypes.includes(file.type)) return 'image';
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  for (const kind of ['document', 'audio', 'video'] as const)
    if (extensions[kind].includes(extension)) return kind;
  return null;
}

export function agentAttachmentAccept(kinds: readonly AgentAttachmentKind[]): string {
  return kinds
    .flatMap((kind) => (kind === 'image' ? imageTypes : extensions[kind].map((ext) => `.${ext}`)))
    .join(',');
}

export function agentAttachmentError(
  file: Pick<File, 'name' | 'type' | 'size'>,
  kinds: readonly AgentAttachmentKind[] = ALL_ATTACHMENT_KINDS,
): string | null {
  const kind = agentAttachmentKind(file);
  if (!kind || !kinds.includes(kind)) return `${file.name} 不是当前支持的附件类型`;
  if (!file.size) return `${file.name} 是空文件`;
  return file.size > MAX_AGENT_ATTACHMENT_BYTES ? `${file.name} 超过 3MB 限制` : null;
}

export function agentAttachmentsError(
  files: readonly Pick<File, 'name' | 'type' | 'size'>[],
  kinds: readonly AgentAttachmentKind[] = ALL_ATTACHMENT_KINDS,
): string | null {
  for (const file of files) {
    const error = agentAttachmentError(file, kinds);
    if (error) return error;
  }
  return files.reduce((total, file) => total + file.size, 0) > MAX_AGENT_ATTACHMENTS_TOTAL_BYTES
    ? '附件总大小不能超过 10MB'
    : null;
}

export async function validateAgentAttachmentImage(file: File): Promise<void> {
  if (agentAttachmentKind(file) !== 'image' || validatedImages.has(file)) return;
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error(`无法读取 ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
  const metadata = inspectAgentAttachmentImage(new Uint8Array(buffer));
  if (!metadata || metadata.contentType !== file.type)
    throw new Error('图片内容与文件类型不匹配，请选择 PNG、JPEG 或 WebP');
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    try {
      await image.decode();
    } catch {
      throw new Error(`${file.name} 无法解码，请重新选择图片`);
    }
    const error = agentImageDimensionError(image.naturalWidth, image.naturalHeight);
    if (error) throw new Error(error);
    validatedImages.add(file);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function validateAgentAttachments(
  files: readonly File[],
  kinds: readonly AgentAttachmentKind[] = ALL_ATTACHMENT_KINDS,
): Promise<void> {
  const error = agentAttachmentsError(files, kinds);
  if (error) throw new Error(error);
  for (const file of files) await validateAgentAttachmentImage(file);
}
